import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {Observable, Subject, Subscription, takeUntil} from "rxjs";
import {DialogService, DynamicDialogRef} from "primeng/dynamicdialog";
import {MessageService} from "primeng/api";
import {EbayService} from "@app/core/services";
import {CommonModule} from "@angular/common";
import {ButtonModule} from "primeng/button";
import {MessageModule} from "primeng/message";
import {ProgressSpinnerModule} from "primeng/progressspinner";
import {EbayMarketplaceOffer, EbayMarketplaceStockOffers, EbayStock} from "@app/core/interfaces/ebay";
import {TableModule} from "primeng/table";
import {TagModule} from "primeng/tag";
import {TooltipModule} from "primeng/tooltip";
import {Nl2brPipe} from "@app/core/pipes";
import {ProductDetailMarketplaceForm} from "@app/features/product/product-detail-marketplace-form/product-detail-marketplace-form";

@Component({
  selector: 'app-product-detail-distribution-ebay-marketplace',
    imports: [
        CommonModule,
        ButtonModule,
        MessageModule,
        ProgressSpinnerModule,
        TableModule,
        TagModule,
        TooltipModule,
        Nl2brPipe
    ],
  templateUrl: './product-detail-distribution-ebay-marketplace.html'
})
export class ProductDetailDistributionEbayMarketplace implements OnDestroy, OnInit {
    @Input() ebayStock!: EbayStock;

    ebayMarketplaceStockOffers!: EbayMarketplaceStockOffers;

    protected apiCall$?: Subscription;
    private destroy$: Subject<any> = new Subject();

    isLoadingData: boolean = true;
    synchronizingIds: string[] = [];
    excludingIds: string[] = [];

    constructor(
        private dialogRef: DynamicDialogRef,
        private messageService: MessageService,
        private dialogService: DialogService,
        private ebayService: EbayService
    ) {}

    ngOnInit(): void {
        this.getOffers();
    }

    getOffers() {
        this.isLoadingData = true;
        this.apiCall$ = this.ebayService.getEbayMarketplaceOffers(this.ebayStock.id)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    this.ebayMarketplaceStockOffers = response;
                    // this.ebayMarketplaceStockOffers = {
                    //     "stock_ebay_id": 102,
                    //     "sku": "rei-15904-20036-160SAA02NPS1SERB",
                    //     "quantite": 7,
                    //     "france": {
                    //         "marketplace_id": "EBAY_FR",
                    //         "sku": "rei-15904-20036-160SAA02NPS1SERB",
                    //         "offer_id": "305106345011",
                    //         "listing_id": "278445139348",
                    //         "publie": true,
                    //         "url": "https://www.ebay.fr/itm/278445139348",
                    //         "prix": 54.0,
                    //         "devise": "EUR"
                    //     },
                    //     "marketplaces": [
                    //         {
                    //             "marketplace_id": "EBAY_IT",
                    //             "nom": "eBay Italie",
                    //             "actif": true,
                    //             "sku": "ita-rei-15904-20036-160SAA02NPS1SERB",
                    //             "offer_id": "305108735011",
                    //             "listing_id": "307221250290",
                    //             "publie": true,
                    //             "url": "https://www.ebay.it/itm/307221250290",
                    //             "prix": 54.0,
                    //             "devise": "EUR",
                    //             "exclue": false,
                    //             "categorie_ebay_id": null,
                    //             "titre": "160SAA02NPS1SERB - ALLEN BRADLEY - 160S-AA02NPS1SERB / Variatore/Avviatore NEW",
                    //             "description": "ALLEN-BRADLEY   160S-AA02NPS1     0.37KW\nINPUT/200V-240V MONO I: 4.8A HZ: 50/60 1100VA\nOUTPUT 200V-230V TRIFASE I: 2.3A  0-240HZ",
                    //             "titre_manuel": true,
                    //             "description_manuelle": false,
                    //             "traduction_langue": "it",
                    //             "traduit_at": "2026-10-13T15:11:24",
                    //             "statut_synchro": "OK",
                    //             "derniere_erreur": null,
                    //             "derniere_synchro_at": "2026-10-15T12:32:51"
                    //         }
                    //     ]
                    // }
                    console.log("getOffers response", response);
                    this.apiCall$?.unsubscribe();
                    this.isLoadingData = false;
                },
                error: (error) => {
                    console.error("getOffers error", error);
                    this.apiCall$?.unsubscribe();
                    this.isLoadingData = false;
                }
            });
    }

    onOpenEditModal(marketplace: EbayMarketplaceOffer): void {
        // console.log("Opening edit modal for marketplace:", marketplace);
        const data = {marketplace: marketplace, ebayStock: this.ebayStock};
        this.dialogService.open(ProductDetailMarketplaceForm, {
            header: `Modification du magasin ${marketplace.nom}`,
            inputValues: data,
            width: '600px',
            modal: true,
            focusOnShow: false,
        });
    }

    hideDialog(): void {
        this.isLoadingData = false;
        this.dialogRef.close();
    }

    synchronizeMarketplace(marketplace: EbayMarketplaceOffer | null): void {
        const synchronisationId = marketplace ? marketplace.marketplace_id : 'all';
        this.synchronizingIds.push(synchronisationId);

        const data = marketplace ? {marketplace_id: marketplace.marketplace_id} : {};

        this.ebayService.postEbayMarketplaceSync(this.ebayStock.id, data)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    // console.log("synchronizeMarketplace response", response);
                    this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Synchronisation terminée avec succès.' });
                    this.synchronizingIds = this.synchronizingIds.filter(id => id !== synchronisationId);
                    this.getOffers();
                },
                error: (error) => {
                    // console.error("synchronizeMarketplace error", error);
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Echec de la synchronisation.' });
                    this.synchronizingIds = this.synchronizingIds.filter(id => id !== synchronisationId);
                }
            });
    }

    isSynchronizingMarketplace(marketplace: EbayMarketplaceOffer | null): boolean {
        const synchronisationId = marketplace ? marketplace.marketplace_id : 'all';
        return this.synchronizingIds.includes(synchronisationId);
    }

    excludingMarketplace(marketplace: EbayMarketplaceOffer, exclude: boolean): void {
        const excludingId = marketplace.marketplace_id;
        this.excludingIds.push(excludingId);
        const data = marketplace ? {marketplace_id: marketplace.marketplace_id} : {};
        let Observable: Observable<any>;

        if(exclude) {
            Observable = this.ebayService.postEbayMarketPlaceInclude(this.ebayStock.id, data);
        } else {
            Observable = this.ebayService.postEbayMarketPlaceExclude(this.ebayStock.id, data);
        }
        Observable
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    this.messageService.add({ severity: 'success', summary: 'Success', detail: exclude ? 'Annonce active sur eBay.' : 'Annonce inactive sur eBay.' });
                    this.excludingIds = this.excludingIds.filter(id => id !== excludingId);
                    this.getOffers();
                },
                error: (error) => {
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: exclude ? 'Echec de l\'activation de l\'annonce sur eBay.' : 'Echec de la désactivation de l\'annonce sur eBay.' });
                    this.excludingIds = this.excludingIds.filter(id => id !== excludingId);
                }
            });
    }

    isExcludingMarketplace(marketplace: EbayMarketplaceOffer): boolean {
        return this.excludingIds.includes(marketplace.marketplace_id);
    }

    ngOnDestroy(): void {
        if (this.destroy$) {
            this.destroy$.next(true);
            this.destroy$.complete();
        }
        if(this.apiCall$) {
            this.apiCall$.unsubscribe();
        }
    }
}
